/**
 * Shared navigation helper for "tap the Gemini logo → go Home".
 *
 * The logo appears in two places — the desktop left sidebar
 * (LeftSidebarContainer) and the app header (CustomHeaderContainer) — and both
 * must reach the Home route reliably. When a stack screen (a pushed modal or
 * detail route) is on top, a bare `router.navigate("/")` can fail to surface
 * Home, so we dismiss the active stack first and defer the navigate by a zero
 * delay so the navigation container finishes its state update before we push.
 *
 * Tapping the logo while already on Home is a no-op so we never stack a
 * duplicate Home entry.
 * @module utils/navigateToHome
 */

import type { Href, useRouter } from "expo-router";
import { Platform } from "react-native";

/** The Home route path. */
export const HOME_ROUTE = "/" as const;

/**
 * The subset of the expo-router instance navigateToHome relies on. Accepting a
 * structural type (rather than the full router) keeps the helper trivially
 * unit-testable without an expo-router mock.
 */
type HomeNavRouter = Pick<
  ReturnType<typeof useRouter>,
  "navigate" | "canDismiss" | "dismissAll"
>;

/**
 * Navigates to the Home route, dismissing any active stack screen first.
 *
 * No-ops when the user is already on Home so the tap never pushes a duplicate
 * Home entry.
 *
 * SE-5259: On web, settings routes are entered via the SE-3726
 * `window.history.pushState` + synthetic `popstate` workaround
 * (`useSettingsActions.webNavigate`). That bypasses React Navigation's stack,
 * so from a settings page `router.canDismiss()` is false and a bare
 * `router.navigate("/")` fails to surface Home — the logo tap appears dead.
 * Driving Home through the same browser-history workaround on web reliably
 * leaves the settings page. Native keeps the dismiss-then-navigate behaviour.
 * @param router - Expo router instance for programmatic navigation
 * @param pathname - Current route pathname (from usePathname), or null
 */
export const navigateToHome = (
  router: HomeNavRouter,
  pathname: string | null
): void => {
  if (pathname === HOME_ROUTE) {
    return;
  }

  if (Platform.OS === "web" && typeof window !== "undefined") {
    window.history.pushState({}, "", HOME_ROUTE);
    window.dispatchEvent(new PopStateEvent("popstate"));
    return;
  }

  if (router.canDismiss()) {
    router.dismissAll();
    setTimeout(() => {
      router.navigate(HOME_ROUTE as Href);
    }, 0);
    return;
  }

  router.navigate(HOME_ROUTE as Href);
};
